import {
  Alert,
  AloftDate,
  PairedCostRouteItemWithQuery,
  schAlertSeverity,
  schAlertStatus,
  schPairedCostRouteItem,
  schQueryParams,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import {
  getLocationTypeSaleDateRequirementMonths,
  isSaleDateOutOfBounds,
} from '../../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getScopedAlertType } from '../utils/compTypeScoping.ts';

export const compSaleDateToSubject: AlertDefinition = ({
  compSequence,
  fullCompSequence,
  propertyDataEntities,
  order,
  compType,
  effectiveDate,
}) => {
  const subjectPropertyData = propertyDataEntities.subject as SubjectPropertyDataState | undefined;
  if (!subjectPropertyData) return [];

  const subjectLocationType =
    subjectPropertyData.subjectData.editableData.propertyIdentification.locationType;

  if (!subjectLocationType) return [];

  const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(subjectLocationType);

  return compSequence
    .map((compID, index) => {
      const compData = propertyDataEntities[compID] as CompPropertyDataState | undefined;
      if (!compData) return undefined;

      const saleMonth = compData.compData.editableData.transaction.saleDate;
      if (!saleMonth || compData.compData.editableData.transaction.status !== 'SettledSale')
        return undefined;

      const saleDate = AloftDate.fromIsoString(`${saleMonth}-01`);

      const saleDateIsOutsideRequiredRange = isSaleDateOutOfBounds(
        saleDate,
        effectiveDate,
        saleDateRequirementMonths,
      );
      const saleDateDifferenceInMonths = Math.abs(
        saleDate.differenceInCalendarMonths(effectiveDate),
      );

      const compIndex = fullCompSequence?.indexOf(compID);

      if (!saleDateIsOutsideRequiredRange) return undefined;

      const alert: Alert = {
        type: getScopedAlertType('compSaleDateToSubject', compType),
        status: schAlertStatus.enum.active,
        severity: schAlertSeverity.enum.warning,
        orderType: ['appraisify'],
        ...(compType ? { compType } : {}),
        message: `Comp ${compIndex !== undefined ? `${compIndex + 1}` : index + 1} exceeds the ${saleDateRequirementMonths} month sale date suggested for ${subjectLocationType} locations (${saleDateDifferenceInMonths} months).`,
        propertyID: compID,
        triggers: getAlertTriggers({
          fixRoute:
            `${schPairedCostRouteItem.enum.features}?${schQueryParams.enum.addComps}=true` as PairedCostRouteItemWithQuery,
          order,
        }),
        fixingInfo: {
          tips: 'Recent sale dates reflect the most relevant market conditions. Transactions closed near the effective date tend to share the same market shifts that influence its value.',
          pairedCostRouteItem:
            `${schPairedCostRouteItem.enum.features}?${schQueryParams.enum.addComps}=true` as PairedCostRouteItemWithQuery,
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
        },
      };

      return alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);
};
